import { formatMoney } from 'common/services/format'
import type { HistoricalDataItem } from 'features/Procedure/state/procedure-api.type'
import type { ColumnsType } from 'antd/es/table'
import { Diagnosis } from './diagnosis'
import styles from './historical-procedures.module.scss'

export type HistoricalProcedureTableRow =
  | (HistoricalDataItem & {
      encounter_date: string
      general_note?: string | null
      rowSpan?: number
      isFirstInGroup?: boolean
      isDescriptionRow?: false
      uniqueKey: string
    })
  | {
      encounter_date: string
      general_note?: string | null
      isDescriptionRow: true
      uniqueKey: string
    }

const formatDate = (date: string) => {
  if (!date) return 'N/A'
  const dateObj = new Date(date)
  return dateObj.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

const TOTAL_COLUMNS = 8

export const createColumns = (
  rowSpanMap: Map<string, number>,
): ColumnsType<HistoricalProcedureTableRow> => [
  {
    title: 'Date',
    dataIndex: 'encounter_date',
    key: 'encounter_date',
    width: 120,
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { rowSpan: rowSpanMap.get(record.encounter_date) || 1, className: styles.dateCell }
      }
      return { rowSpan: 0 }
    },
    render: (date: string, record) => {
      if (!record.isDescriptionRow) return null
      return <span className={styles.dateText}>{formatDate(date)}</span>
    },
  },
  {
    title: 'Diagnosis',
    dataIndex: 'justify_codes',
    key: 'justify_codes',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: TOTAL_COLUMNS - 1, className: styles.descriptionCell }
      }
      return {}
    },
    render: (codes: string[], record) => {
      if (record.isDescriptionRow) {
        return (
          <span>
            <span className={styles.descriptionText}>General Note: </span>
            {record.general_note || 'N/A'}
          </span>
        )
      }
      if (!codes || codes.length === 0) return 'N/A'
      return <Diagnosis justifyCodes={codes} />
    },
  },
  {
    title: 'Procedure',
    dataIndex: 'code_text',
    key: 'code_text',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (text: string, record) => {
      if (record.isDescriptionRow) return null
      return text || 'N/A'
    },
  },
  {
    title: 'Units',
    dataIndex: 'procedure_units',
    key: 'procedure_units',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (units: string, record) => {
      if (record.isDescriptionRow) return null
      return units || 'N/A'
    },
  },
  {
    title: 'Qty',
    dataIndex: 'units',
    key: 'units',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (units: number, record) => {
      if (record.isDescriptionRow) return null
      return units ?? 'N/A'
    },
  },
  {
    title: 'Price',
    dataIndex: 'fee',
    key: 'fee',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (fee: number, record) => {
      if (record.isDescriptionRow) return null
      return fee != null ? formatMoney(fee) : 'N/A'
    },
  },
  {
    title: 'Discount',
    dataIndex: 'discount_amount',
    key: 'discount_amount',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (discountAmount: number | undefined, record) => {
      if (record.isDescriptionRow) return null
      if (!discountAmount) return 'N/A'
      return formatMoney(discountAmount)
    },
  },
  {
    title: 'Total',
    dataIndex: 'total_price',
    key: 'total_price',
    onCell: (record) => {
      if (record.isDescriptionRow) {
        return { colSpan: 0 }
      }
      return {}
    },
    render: (totalPrice: number, record) => {
      if (record.isDescriptionRow) return null
      return totalPrice != null ? formatMoney(totalPrice) : 'N/A'
    },
  },
]
